import { parseColor } from '../../lib/color';
import { SAFE_DPR } from '../constants';
import { normalizeDimensions } from '../shape';
import { getBackNodesHash } from '../utils/layers';
import { resolveRadius } from '../utils/radius';
import { ShaderDisplacementGenerator, fragmentShaders } from './liquidUtils';

// WebGL shader for liquid glass displacement effect
const vertexShaderSource = `
    attribute vec2 a_position;
    attribute vec2 a_texCoord;
    varying vec2 v_texCoord;
    
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
    }
`;

const fragmentShaderSource = `
    precision mediump float;
    
    uniform sampler2D u_texture;
    uniform sampler2D u_displacementMap;
    uniform vec2 u_resolution;
    uniform float u_displacementScale;
    uniform float u_aberrationIntensity;
    uniform float u_blur;
    uniform int u_blurMode; // 0 = uniform, 1 = progressive
    uniform float u_scaleFactor;
    uniform float u_saturation;
    uniform float u_edgeCurvature;
    uniform vec2 u_elementSize;
    uniform vec4 u_cornerRadius; // tl, tr, bl, br
    uniform vec3 u_innerShadowColor;
    uniform float u_innerShadowIntensity;
    uniform float u_innerShadowSpread;
    uniform float u_innerShadowBlur;
    uniform vec2 u_innerShadowOffset;
    
    varying vec2 v_texCoord;

    // Pseudo-random number generator for frosted effect
    float rand(vec2 co){
        return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
    }
    
    // Distance to rounded rectangle
    float roundedRectDistance(vec2 p, vec2 size, vec4 radius) {
        vec2 q = abs(p) - size + vec2(radius.x);
        vec2 r = vec2(radius.x);
        
        // Select appropriate corner radius based on quadrant
        if (p.x > 0.0 && p.y > 0.0) r.x = radius.y; // top-right
        else if (p.x < 0.0 && p.y > 0.0) r.x = radius.x; // top-left
        else if (p.x < 0.0 && p.y < 0.0) r.x = radius.z; // bottom-left
        else if (p.x > 0.0 && p.y < 0.0) r.x = radius.w; // bottom-right
        
        return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r.x;
    }
    
    // Calculate mask for rounded corners
    float calculateCornerMask(vec2 uv) {
        // Convert UV to centered coordinates
        vec2 pos = (uv - 0.5) * u_elementSize;
        vec2 size = u_elementSize * 0.5;
        
        float dist = roundedRectDistance(pos, size, u_cornerRadius);
        
        // Smooth edge with anti-aliasing
        float edgeSmooth = 1.0 / min(u_elementSize.x, u_elementSize.y) * 2.0;
        return 1.0 - smoothstep(-edgeSmooth, edgeSmooth, dist);
    }
    
    // High-quality background blur for a "frosted glass" effect using random sampling
    vec4 backgroundBlur(sampler2D tex, vec2 uv, vec2 resolution, float blurRadius, int blurMode) {
        // Early return for no blur
        if (blurRadius <= 0.0) {
            return texture2D(tex, uv);
        }
        
        vec2 texelSize = 1.0 / resolution;
        
        // The input blurRadius is used directly as the blur strength.
        float pixelRadius = blurRadius;
        
        // Apply scale factor to maintain blur consistency across zoom levels
        pixelRadius *= u_scaleFactor;
        
        // Limit maximum blur radius for performance
        pixelRadius = min(pixelRadius, 50.0);
        
        // Progressive blur - blur intensity varies from center to edge
        float progressiveMultiplier = 1.0;
        if (blurMode == 1) {
            // Calculate distance from center for progressive effect
            vec2 centerDist = abs(uv - 0.5) * 2.0;
            float distFromCenter = max(centerDist.x, centerDist.y);
            // Progressive multiplier: less blur in center, more at edges
            progressiveMultiplier = mix(0.2, 1.0, smoothstep(0.0, 1.0, distFromCenter));
        }
        
        float effectiveRadius = pixelRadius * progressiveMultiplier;

        // Early exit if blur is negligible
        if (effectiveRadius < 0.1) {
            return texture2D(tex, uv);
        }
        
        vec4 color = vec4(0.0);
        const int NUM_SAMPLES = 24; // Balance between quality and performance
        
        // Use a pseudo-random sampling pattern (Monte Carlo) for a frosted/atomized look
        for (int i = 0; i < NUM_SAMPLES; i++) {
            // Generate a random point in a disc
            float angle = rand(uv + float(i) * 0.01) * 6.283185;
            float radius = sqrt(rand(uv - float(i) * 0.01)) * effectiveRadius;
            
            vec2 offset = vec2(cos(angle), sin(angle)) * radius;
            vec2 sampleUV = uv + offset * texelSize;
            
            // Clamp to avoid reading outside texture
            sampleUV = clamp(sampleUV, vec2(0.0), vec2(1.0));
            
            color += texture2D(tex, sampleUV);
        }
        
        return color / float(NUM_SAMPLES);
    }
    
    // Sample background color from behind the glass for edge glow
    vec3 sampleBackgroundColor(vec2 uv, float sampleRadius) {
        vec3 avgColor = vec3(0.0);
        float totalWeight = 0.0;
        const int SAMPLES = 8;
        
        for (int i = 0; i < SAMPLES; i++) {
            float angle = float(i) * 6.283185 / float(SAMPLES);
            vec2 sampleUV = uv + vec2(cos(angle), sin(angle)) * sampleRadius;
            sampleUV = clamp(sampleUV, vec2(0.0), vec2(1.0));
            
            vec3 sampleColor = texture2D(u_texture, sampleUV).rgb;
            float weight = 1.0;
            avgColor += sampleColor * weight;
            totalWeight += weight;
        }
        
        return avgColor / totalWeight;
    }
    
    // Enhanced edge glow calculation with background color adaptation
    vec3 calculateAdaptiveEdgeGlow(vec2 uv, vec3 baseColor) {
        // Distance from edges considering corner radius
        vec2 pos = (uv - 0.5) * u_elementSize;
        vec2 size = u_elementSize * 0.5;
        
        float dist = roundedRectDistance(pos, size, u_cornerRadius);
        
        // Scale-independent glow width
        float baseGlowWidth = 12.0;
        float glowWidth = baseGlowWidth * u_edgeCurvature;
        float edgeFactor = 1.0 - smoothstep(0.0, glowWidth, abs(dist));
        
        // Apply curvature to edge intensity
        float curvaturePower = mix(1.5, 0.8, u_edgeCurvature);
        edgeFactor = pow(edgeFactor, curvaturePower);
        
        // Sample background color behind the glass at different distances
        vec2 texelSize = 1.0 / u_resolution;
        float sampleRadius = 3.0 * texelSize.x * u_scaleFactor;
        vec3 backgroundNear = sampleBackgroundColor(uv, sampleRadius);
        vec3 backgroundFar = sampleBackgroundColor(uv, sampleRadius * 2.0);
        
        // Create adaptive glow color based on background
        vec3 adaptiveGlowColor = mix(backgroundNear, backgroundFar, 0.4);
        
        // Brighten and add luminosity to the background color
        adaptiveGlowColor = pow(adaptiveGlowColor, vec3(0.8)); // Gamma correction for brightness
        adaptiveGlowColor *= 1.5; // Increase brightness
        
        // Add subtle color shift for more interesting edge lighting
        vec3 coolTint = vec3(0.9, 0.95, 1.1);
        vec3 warmTint = vec3(1.1, 1.0, 0.9);
        
        // Use position-based tinting for variety
        float tintFactor = sin(uv.x * 3.14159) * cos(uv.y * 3.14159) * 0.5 + 0.5;
        vec3 colorTint = mix(coolTint, warmTint, tintFactor);
        adaptiveGlowColor *= colorTint;
        
        // Ensure the glow color is always brighter than the base
        float baseLuminance = dot(baseColor, vec3(0.299, 0.587, 0.114));
        float glowLuminance = dot(adaptiveGlowColor, vec3(0.299, 0.587, 0.114));
        if (glowLuminance < baseLuminance + 0.2) {
            adaptiveGlowColor = mix(adaptiveGlowColor, vec3(1.0), 0.3);
        }
        
        return adaptiveGlowColor;
    }

    // Inner shadow calculation (CSS box-shadow: inset style)
    float calculateInnerShadow(vec2 uv, out float shadowMask) {
        shadowMask = 0.0;
        
        if (u_innerShadowIntensity <= 0.0) {
            return 0.0;
        }

        // Convert UV to element-relative coordinates
        vec2 pos = (uv - 0.5) * u_elementSize;
        vec2 size = u_elementSize * 0.5;
        
        // Apply shadow offset (convert from element space to UV space)
        vec2 offsetInElementSpace = u_innerShadowOffset;
        vec2 shadowPos = pos - offsetInElementSpace;
        
        // Calculate distance to the outer edge of the element
        float outerDist = roundedRectDistance(pos, size, u_cornerRadius);
        
        // Calculate distance to the shadow-caster edge (offset and spread applied)
        vec2 shadowSize = size - vec2(u_innerShadowSpread);
        float shadowDist = roundedRectDistance(shadowPos, shadowSize, u_cornerRadius);
        
        // Element mask - only apply shadow inside the element
        float elementMask = 1.0 - smoothstep(-2.0, 0.0, outerDist);
        
        // Shadow calculation: visible when we're inside the element but outside the shadow-caster
        // The shadow intensity decreases as we move away from the shadow-caster edge
        float shadowBlurScaled = u_innerShadowBlur * u_scaleFactor;
        shadowBlurScaled = max(shadowBlurScaled, 0.5); // Minimum blur for smooth edges
        
        // Shadow mask: 1.0 where shadow should be cast, 0.0 where it shouldn't
        shadowMask = elementMask * smoothstep(-shadowBlurScaled, 0.0, shadowDist);
        
        // Shadow intensity based on distance from shadow edge
        float shadowIntensity = shadowMask * u_innerShadowIntensity;
        
        // Add distance-based falloff for more realistic shadow
        float falloff = 1.0 - smoothstep(0.0, shadowBlurScaled, shadowDist);
        shadowIntensity *= falloff;
        
        return shadowIntensity;
    }
    
    // Liquid glass refraction calculation
    vec2 calculateRefraction(vec2 uv, vec4 displacement) {
        // Displacement vector with scale control
        vec2 displacementVector = (displacement.rg - 0.5) * 2.0 * u_displacementScale;
        
        // Scale-independent wave patterns - use fixed base scale
        float waveScale = min(u_elementSize.x, u_elementSize.y) / 100.0;
        float wave1 = sin(uv.x * 12.0 * waveScale + displacement.b * 6.28) * (2.0 / min(u_elementSize.x, u_elementSize.y)) * u_displacementScale;
        float wave2 = cos(uv.y * 8.0 * waveScale + displacement.a * 6.28) * (1.5 / min(u_elementSize.x, u_elementSize.y)) * u_displacementScale;
        
        // Apply edge curvature
        vec2 pos = (uv - 0.5) * u_elementSize;
        vec2 size = u_elementSize * 0.5;
        float edgeDist = abs(roundedRectDistance(pos, size, u_cornerRadius));
        float edgeFactor = 1.0 - smoothstep(0.0, 30.0, edgeDist);
        
        // Scale-independent curvature multiplier
        float curvatureMultiplier = 1.0 + edgeFactor * u_edgeCurvature * 2.0;
        wave1 *= curvatureMultiplier;
        wave2 *= curvatureMultiplier;
        
        displacementVector += vec2(wave1, wave2);
        
        return displacementVector;
    }
    
    // Apply saturation adjustment
    vec3 applySaturation(vec3 color, float saturation) {
        // Convert RGB to HSL and adjust saturation
        float maxComponent = max(max(color.r, color.g), color.b);
        float minComponent = min(min(color.r, color.g), color.b);
        float luminance = (maxComponent + minComponent) * 0.5;
        
        // Calculate saturation
        float currentSaturation = 0.0;
        if (luminance > 0.0 && luminance < 1.0) {
            float delta = maxComponent - minComponent;
            currentSaturation = delta / (1.0 - abs(2.0 * luminance - 1.0));
        }
        
        // Apply saturation multiplier
        vec3 grayColor = vec3(luminance);
        return mix(grayColor, color, saturation);
    }
    
    void main() {
        vec2 uv = v_texCoord;
        
        // Calculate corner mask
        float cornerMask = calculateCornerMask(uv);
        
        // Early exit if outside rounded rectangle
        if (cornerMask < 0.001) {
            gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
            return;
        }
        
        // Sample displacement map
        vec4 displacement = texture2D(u_displacementMap, uv);
        
        // Calculate liquid glass refraction
        vec2 refraction = calculateRefraction(uv, displacement);
        vec2 displacementVector = refraction * 0.002; // Base scale factor
        
        // Apply chromatic aberration with proper scaling
        vec2 texelSize = 1.0 / u_resolution;
        float aberrationStrength = u_aberrationIntensity * u_scaleFactor * 3.0; // Increased base multiplier
        
        // Ensure minimum aberration effect when enabled
        if (u_aberrationIntensity > 0.0) {
            aberrationStrength = max(aberrationStrength, 0.5);
        }
        
        vec2 aberrationOffset = vec2(aberrationStrength * texelSize.x, 0.0);
        
        vec2 redUV = uv + displacementVector * 1.1 + aberrationOffset;
        vec2 greenUV = uv + displacementVector;
        vec2 blueUV = uv + displacementVector * 0.9 - aberrationOffset;
        
        // Clamp UV coordinates
        redUV = clamp(redUV, 0.001, 0.999);
        greenUV = clamp(greenUV, 0.001, 0.999);
        blueUV = clamp(blueUV, 0.001, 0.999);
        
        // Sample the texture for glass effect without blur first
        float red = texture2D(u_texture, redUV).r;
        float green = texture2D(u_texture, greenUV).g;
        float blue = texture2D(u_texture, blueUV).b;
        float alpha = texture2D(u_texture, uv).a;
        
        // Create liquid glass color effect
        vec3 glassColor = vec3(red, green, blue);
        
        // Apply saturation adjustment
        glassColor = applySaturation(glassColor, u_saturation);
        
        // Scale-independent glass tint and brightness - use fixed values
        glassColor = mix(glassColor, vec3(0.9, 0.95, 1.0), 0.15);
        glassColor *= 1.3; // Fixed brightness multiplier
        
        // Scale-independent iridescence - use fixed scaling
        float iridescence = sin(displacement.r * 6.28 + uv.x * 10.0) * 0.1;
        float iridescenceIntensity = 1.0;
        glassColor += vec3(iridescence * 0.2, iridescence * 0.1, -iridescence * 0.1) * iridescenceIntensity;
        
        // Apply blur after glass effect
        vec4 finalColor = vec4(glassColor, alpha);
        if (u_blur > 0.0) {
            // Create a temporary texture-like representation for blur
            // Since we can't create new textures in fragment shader, we simulate blur on the result
            vec4 blurredResult = backgroundBlur(u_texture, uv, u_resolution, u_blur, u_blurMode);
            
            // Apply the glass effect transformations to the blurred result
            vec4 blurredRed = backgroundBlur(u_texture, redUV, u_resolution, u_blur, u_blurMode);
            vec4 blurredGreen = backgroundBlur(u_texture, greenUV, u_resolution, u_blur, u_blurMode);
            vec4 blurredBlue = backgroundBlur(u_texture, blueUV, u_resolution, u_blur, u_blurMode);
            
            vec3 blurredGlassColor = vec3(blurredRed.r, blurredGreen.g, blurredBlue.b);
            blurredGlassColor = applySaturation(blurredGlassColor, u_saturation);
            blurredGlassColor = mix(blurredGlassColor, vec3(0.9, 0.95, 1.0), 0.15);
            blurredGlassColor *= 1.3;
            blurredGlassColor += vec3(iridescence * 0.2, iridescence * 0.1, -iridescence * 0.1) * iridescenceIntensity;
            
            finalColor = vec4(blurredGlassColor, blurredResult.a);
        }
        
        // Apply inner shadow
        float shadowMask;
        float innerShadow = calculateInnerShadow(uv, shadowMask);
        finalColor.rgb = mix(finalColor.rgb, u_innerShadowColor, innerShadow);

        // Calculate adaptive edge glow that uses background colors
        vec3 adaptiveGlowColor = calculateAdaptiveEdgeGlow(uv, finalColor.rgb);
        
        // Calculate edge distance for glow intensity
        vec2 pos = (uv - 0.5) * u_elementSize;
        vec2 size = u_elementSize * 0.5;
        float dist = roundedRectDistance(pos, size, u_cornerRadius);
        float baseGlowWidth = 12.0;
        float glowWidth = baseGlowWidth * u_edgeCurvature;
        float edgeGlow = 1.0 - smoothstep(0.0, glowWidth, abs(dist));
        float curvaturePower = mix(1.5, 0.8, u_edgeCurvature);
        edgeGlow = pow(edgeGlow, curvaturePower);
        
        // Apply the adaptive glow with varying intensity
        float glowIntensity = 0.7 * (1.0 + u_edgeCurvature * 0.6);
        finalColor.rgb = mix(finalColor.rgb, adaptiveGlowColor, edgeGlow * glowIntensity);
        
        // Apply edge glow to iridescence as well
        float iridescenceGlowIntensity = 1.0 + edgeGlow * u_edgeCurvature * 0.5;
        
        // Scale-independent alpha enhancement - use fixed values
        float alphaEnhancement = 0.3 * (1.0 + u_edgeCurvature * 0.3);
        finalColor.a = mix(finalColor.a, 1.0, edgeGlow * alphaEnhancement);
        
        // Apply corner mask to final alpha
        finalColor.a *= cornerMask;
        
        gl_FragColor = finalColor;
    }
`;

// WebGL context and shader management
class LiquidGlassRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!this.gl) {
      console.warn('WebGL not supported, falling back to canvas filters');
      return null;
    }

    this.program = null;
    this.uniforms = {};
    this.attributes = {};
    this.buffers = {};
    this.textures = {};
    this.displacementTexture = null;
    this.displacementReady = false;

    this.init();
  }

  init() {
    const gl = this.gl;

    // Create shaders
    const vertexShader = this.createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    // Create program
    this.program = gl.createProgram();
    gl.attachShader(this.program, vertexShader);
    gl.attachShader(this.program, fragmentShader);
    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(this.program));
      return;
    }

    // Get attribute and uniform locations
    this.attributes.position = gl.getAttribLocation(this.program, 'a_position');
    this.attributes.texCoord = gl.getAttribLocation(this.program, 'a_texCoord');

    // Core uniforms
    this.uniforms.texture = gl.getUniformLocation(this.program, 'u_texture');
    this.uniforms.displacementMap = gl.getUniformLocation(this.program, 'u_displacementMap');
    this.uniforms.resolution = gl.getUniformLocation(this.program, 'u_resolution');

    // Effect control uniforms
    this.uniforms.displacementScale = gl.getUniformLocation(this.program, 'u_displacementScale');
    this.uniforms.aberrationIntensity = gl.getUniformLocation(
      this.program,
      'u_aberrationIntensity'
    );
    this.uniforms.blur = gl.getUniformLocation(this.program, 'u_blur');
    this.uniforms.blurMode = gl.getUniformLocation(this.program, 'u_blurMode');
    this.uniforms.scaleFactor = gl.getUniformLocation(this.program, 'u_scaleFactor');
    this.uniforms.saturation = gl.getUniformLocation(this.program, 'u_saturation');
    this.uniforms.edgeCurvature = gl.getUniformLocation(this.program, 'u_edgeCurvature');

    // Element geometry uniforms
    this.uniforms.elementSize = gl.getUniformLocation(this.program, 'u_elementSize');
    this.uniforms.cornerRadius = gl.getUniformLocation(this.program, 'u_cornerRadius');

    // Inner shadow uniforms
    this.uniforms.innerShadowColor = gl.getUniformLocation(this.program, 'u_innerShadowColor');
    this.uniforms.innerShadowIntensity = gl.getUniformLocation(
      this.program,
      'u_innerShadowIntensity'
    );
    this.uniforms.innerShadowSpread = gl.getUniformLocation(this.program, 'u_innerShadowSpread');
    this.uniforms.innerShadowBlur = gl.getUniformLocation(this.program, 'u_innerShadowBlur');
    this.uniforms.innerShadowOffset = gl.getUniformLocation(this.program, 'u_innerShadowOffset');

    // Create buffers
    this.createBuffers();

    // Prepare a neutral displacement so the first render never no-ops
    this.createNeutralDisplacementTexture();

    // Create real displacement texture (async)
    this.createDisplacementTexture();
  }

  createShader(type, source) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  createBuffers() {
    const gl = this.gl;

    // Position buffer (full screen quad)
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

    this.buffers.position = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    // Texture coordinate buffer
    const texCoords = new Float32Array([0, 1, 1, 1, 0, 0, 1, 0]);

    this.buffers.texCoord = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.texCoord);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);
  }

  createNeutralDisplacementTexture(width, height) {
    if (!this.gl) return;
    const gl = this.gl;
    const w = width || this.canvas.width || 1;
    const h = height || this.canvas.height || 1;
    const size = w * h * 4;
    const data = new Uint8Array(size);
    for (let i = 0; i < size; i += 4) {
      data[i] = 128; // R
      data[i + 1] = 128; // G
      data[i + 2] = 128; // B
      data[i + 3] = 255; // A
    }
    if (!this.displacementTexture) {
      this.displacementTexture = gl.createTexture();
    }
    gl.bindTexture(gl.TEXTURE_2D, this.displacementTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    this.displacementReady = false;
  }

  createDisplacementTexture() {
    if (!this.gl) return;

    // Generate shader-based displacement map
    const generator = new ShaderDisplacementGenerator({
      width: this.canvas.width,
      height: this.canvas.height,
      fragment: fragmentShaders.liquidGlass,
    });

    const displacementCanvas = document.createElement('canvas');
    displacementCanvas.width = this.canvas.width;
    displacementCanvas.height = this.canvas.height;
    const ctx = displacementCanvas.getContext('2d');

    // Generate displacement map
    const dataUrl = generator.updateShader();
    generator.destroy();

    // Load displacement map as image
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);

      // Create WebGL texture from displacement map
      const gl = this.gl;
      if (!this.displacementTexture) {
        this.displacementTexture = gl.createTexture();
      }
      gl.bindTexture(gl.TEXTURE_2D, this.displacementTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, displacementCanvas);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      this.displacementReady = true;
    };
    img.src = dataUrl;
  }

  createTextureFromCanvas(sourceCanvas) {
    const gl = this.gl;
    const texture = gl.createTexture();

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    return texture;
  }

  render(sourceCanvas, options = {}) {
    if (!this.gl || !this.program || !this.displacementTexture) return;

    const gl = this.gl;
    const {
      displacementScale = 1.0,
      aberrationIntensity = 1.0,
      blur = 1.0,
      blurMode = 0,
      scaleFactor = 1.0,
      saturation = 1.0,
      edgeCurvature = 1.0,
      elementSize = { width: 100, height: 100 },
      cornerRadius = { tl: 0, tr: 0, bl: 0, br: 0 },
      innerShadowColor = [0.0, 0.0, 0.0],
      innerShadowIntensity = 0.0,
      innerShadowSpread = 0.0,
      innerShadowBlur = 0.0,
      innerShadowOffset = [0.0, 0.0],
    } = options;

    // Set viewport
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Use shader program
    gl.useProgram(this.program);

    // Create texture from source canvas
    const texture = this.createTextureFromCanvas(sourceCanvas);

    // Bind main texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(this.uniforms.texture, 0);

    // Bind displacement texture
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.displacementTexture);
    gl.uniform1i(this.uniforms.displacementMap, 1);

    // Set core uniforms
    gl.uniform2f(this.uniforms.resolution, this.canvas.width, this.canvas.height);

    // Set effect control uniforms
    gl.uniform1f(this.uniforms.displacementScale, displacementScale);
    gl.uniform1f(this.uniforms.aberrationIntensity, aberrationIntensity);
    gl.uniform1f(this.uniforms.blur, blur);
    gl.uniform1i(this.uniforms.blurMode, blurMode);
    gl.uniform1f(this.uniforms.scaleFactor, scaleFactor);
    gl.uniform1f(this.uniforms.saturation, saturation);
    gl.uniform1f(this.uniforms.edgeCurvature, edgeCurvature);

    // Set element geometry uniforms
    gl.uniform2f(this.uniforms.elementSize, elementSize.width, elementSize.height);
    gl.uniform4f(
      this.uniforms.cornerRadius,
      cornerRadius.tl,
      cornerRadius.tr,
      cornerRadius.bl,
      cornerRadius.br
    );

    // Set inner shadow uniforms
    gl.uniform3fv(this.uniforms.innerShadowColor, innerShadowColor);
    gl.uniform1f(this.uniforms.innerShadowIntensity, innerShadowIntensity);
    gl.uniform1f(this.uniforms.innerShadowSpread, innerShadowSpread);
    gl.uniform1f(this.uniforms.innerShadowBlur, innerShadowBlur);
    gl.uniform2fv(this.uniforms.innerShadowOffset, innerShadowOffset);

    // Set up attributes
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    gl.enableVertexAttribArray(this.attributes.position);
    gl.vertexAttribPointer(this.attributes.position, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.texCoord);
    gl.enableVertexAttribArray(this.attributes.texCoord);
    gl.vertexAttribPointer(this.attributes.texCoord, 2, gl.FLOAT, false, 0, 0);

    // Draw
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Cleanup
    gl.deleteTexture(texture);
  }

  destroy() {
    if (!this.gl) return;

    const gl = this.gl;

    if (this.program) {
      gl.deleteProgram(this.program);
    }

    if (this.displacementTexture) {
      gl.deleteTexture(this.displacementTexture);
    }

    Object.values(this.buffers).forEach((buffer) => {
      if (buffer) gl.deleteBuffer(buffer);
    });

    Object.values(this.textures).forEach((texture) => {
      if (texture) gl.deleteTexture(texture);
    });
  }
}

// Get or create a canvas for a specific node
function getNodeCanvas(backdropCache, nodeId, width, height, type = 'temp') {
  if (!backdropCache[nodeId]) {
    backdropCache[nodeId] = {};
  }

  let canvas = backdropCache[nodeId][type];
  if (!canvas) {
    canvas = document.createElement('canvas');
    backdropCache[nodeId][type] = canvas;
  }

  // Only resize if dimensions changed to avoid unnecessary operations
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }

  return canvas;
}

export function drawLiquidGlassEffect(renderContext, node) {
  const render = () => {
    // This will be called after the effect is applied
  };

  if (!node.backdropFilter || !node.backdropFilter.includes('liquid-glass')) {
    return render();
  }

  drawLiquidGlassEffectImpl(renderContext, node);
  render();
}

function drawLiquidGlassEffectImpl(renderContext, node) {
  const { context } = renderContext;
  const { scale: scaleProvider } = renderContext.providers;
  const scaleValue = scaleProvider.scale;
  const backdropCache = renderContext.cache.backdropCache || {};
  renderContext.cache.backdropCache = backdropCache;

  const box = renderContext.api.getNodeBox(node);
  const x = node.w < 0 ? node.x + node.w : node.x;
  const y = node.h < 0 ? node.y + node.h : node.y;
  const w = Math.abs(node.w);
  const h = Math.abs(node.h);

  // Early exit if dimensions are too small
  if (w < 1 || h < 1) {
    return;
  }

  context.save();

  try {
    // 使用原始元素尺寸作为基准，不受缩放影响
    const originalElementWidth = w;
    const originalElementHeight = h;

    const canvasWidth = Math.ceil(w * scaleValue);
    const canvasHeight = Math.ceil(h * scaleValue);

    const sourceX = Math.floor(box.x * scaleValue + (scaleProvider.translateX || 0));
    const sourceY = Math.floor(box.y * scaleValue + (scaleProvider.translateY || 0));

    // Store original values for calculating adjustments
    const originalSourceX = sourceX;
    const originalSourceY = sourceY;
    const originalCanvasWidth = canvasWidth;
    const originalCanvasHeight = canvasHeight;

    // Get canvas bounds
    const maxCanvasWidth = context.canvas.width;
    const maxCanvasHeight = context.canvas.height;

    // Calculate the intersection of the node area with the canvas bounds
    const intersectionLeft = Math.max(0, sourceX);
    const intersectionTop = Math.max(0, sourceY);
    const intersectionRight = Math.min(maxCanvasWidth, sourceX + canvasWidth);
    const intersectionBottom = Math.min(maxCanvasHeight, sourceY + canvasHeight);

    // Calculate the actual area we can capture from the canvas
    const captureWidth = Math.max(0, intersectionRight - intersectionLeft);
    const captureHeight = Math.max(0, intersectionBottom - intersectionTop);

    // Early exit if there's no intersection with the canvas
    if (captureWidth <= 0 || captureHeight <= 0) {
      return;
    }

    // Keep offscreen size unchanged; record intersection offset inside the offscreen
    const offsetXInOffscreen = intersectionLeft - originalSourceX;
    const offsetYInOffscreen = intersectionTop - originalSourceY;

    const zoom = scaleProvider.scale * SAFE_DPR;

    const isScaling = scaleProvider.scaling;

    // Create cache key for the liquid glass effect result
    const cacheKey = getBackNodesHash(renderContext, node);

    // Check if we have a cached result for this exact configuration
    if (!backdropCache.liquidGlassResultCache) {
      backdropCache.liquidGlassResultCache = {};
    }

    const exportMode = renderContext.mode === 'export';

    let filteredCanvas;
    const cacheItem = backdropCache.liquidGlassResultCache[node.id];
    const isZoomMatch = cacheItem && Math.abs(cacheItem.zoom - zoom) < 1;
    if (
      !exportMode &&
      cacheItem &&
      cacheItem.key === cacheKey &&
      (isZoomMatch || isScaling) &&
      cacheItem.osx === originalSourceX &&
      cacheItem.osy === originalSourceY &&
      cacheItem.ow === originalCanvasWidth &&
      cacheItem.oh === originalCanvasHeight
    ) {
      filteredCanvas = cacheItem.canvas;
    } else {
      // Clean up old cache item
      if (cacheItem) {
        if (cacheItem.canvas) {
          cacheItem.canvas.width = 0;
          cacheItem.canvas.height = 0;
        }
      }

      // Only apply filter if the node is at least partially visible on the canvas
      if (
        !(
          sourceX + canvasWidth <= 0 ||
          sourceX >= context.canvas.width ||
          sourceY + canvasHeight <= 0 ||
          sourceY >= context.canvas.height
        )
      ) {
        // Create or get WebGL renderer for this node
        if (!backdropCache[node.id]) {
          backdropCache[node.id] = {};
        }

        if (!backdropCache[node.id].liquidGlassRenderer) {
          const glCanvas = document.createElement('canvas');
          glCanvas.width = canvasWidth;
          glCanvas.height = canvasHeight;
          backdropCache[node.id].liquidGlassRenderer = new LiquidGlassRenderer(glCanvas);
        }

        const renderer = backdropCache[node.id].liquidGlassRenderer;

        if (renderer && renderer.gl) {
          // Resize WebGL canvas if needed for full offscreen size
          if (
            renderer.canvas.width !== originalCanvasWidth ||
            renderer.canvas.height !== originalCanvasHeight
          ) {
            renderer.canvas.width = originalCanvasWidth;
            renderer.canvas.height = originalCanvasHeight;
            // Recreate displacement texture with new dimensions (neutral first, then real)
            renderer.createNeutralDisplacementTexture(originalCanvasWidth, originalCanvasHeight);
            renderer.createDisplacementTexture();
          }

          // Get background intersection area from the main canvas
          const imageData = context.getImageData(
            intersectionLeft,
            intersectionTop,
            captureWidth,
            captureHeight
          );

          // Use node-level cached canvases for full offscreen size
          const tempCanvas = getNodeCanvas(
            backdropCache,
            node.id,
            originalCanvasWidth,
            originalCanvasHeight,
            'temp'
          );
          const tempContext = tempCanvas.getContext('2d');

          // Clear and set the image data
          tempContext.clearRect(0, 0, originalCanvasWidth, originalCanvasHeight);
          // Place the captured region into the correct offset within the full offscreen
          tempContext.putImageData(imageData, offsetXInOffscreen, offsetYInOffscreen);

          // Parse liquid glass parameters from backdropFilter
          const filterParams = parseLiquidGlassFilter(node.backdropFilter);
          const radius = resolveRadius(node.w, node.h, node.radius);

          // Render liquid glass effect with scale-independent parameters
          renderer.render(tempCanvas, {
            displacementScale: filterParams.displacementScale,
            aberrationIntensity: filterParams.aberrationIntensity,
            // Keep blur consistent across zoom: pass screen-pixel radius directly and use scaleFactor=1.0
            blur: filterParams.blur,
            scaleFactor: 1.0,
            saturation: filterParams.saturation,
            edgeCurvature: filterParams.edgeCurvature,
            innerShadowColor: filterParams.innerShadowColor,
            innerShadowIntensity: filterParams.innerShadowIntensity,
            innerShadowSpread: filterParams.innerShadowSpread,
            innerShadowBlur: filterParams.innerShadowBlur,
            innerShadowOffset: filterParams.innerShadowOffset,
            cornerRadius: {
              tl: radius.topLeft,
              tr: radius.topRight,
              bl: radius.bottomLeft,
              br: radius.bottomRight,
            },
            elementSize: {
              width: originalElementWidth,
              height: originalElementHeight,
            },
          });

          // Create a cached result canvas
          filteredCanvas = document.createElement('canvas');
          filteredCanvas.width = originalCanvasWidth;
          filteredCanvas.height = originalCanvasHeight;
          const filteredContext = filteredCanvas.getContext('2d');
          filteredContext.drawImage(renderer.canvas, 0, 0);

          // Cache the filtered result
          backdropCache.liquidGlassResultCache[node.id] = {
            zoom: zoom,
            key: cacheKey,
            canvas: filteredCanvas,
            osx: originalSourceX,
            osy: originalSourceY,
            ow: originalCanvasWidth,
            oh: originalCanvasHeight,
          };
        }
      }
    }

    if (filteredCanvas) {
      // Apply clipping for rounded corners
      clipRoundedRect(context, node);

      // Draw full offscreen (original size). Canvas will clip overflow based on the current viewport and our rounded-rect clip.
      context.drawImage(filteredCanvas, x, y, w, h);

      // Debug visualization
      if (renderContext.debug) {
        context.strokeStyle = 'rgba(0, 255, 255, 0.8)';
        context.lineWidth = 2;
        context.strokeRect(x + 1, y + 1, w - 2, h - 2);
      }
    }
  } catch (error) {
    // getImageData can throw security errors for tainted canvases or other issues.
    // Silently fail to avoid breaking the render pipeline
    console.warn('Liquid glass effect error:', error);
  } finally {
    context.restore();
  }
}

export function parseLiquidGlassFilter(filterString) {
  const defaults = {
    displacementScale: 70,
    aberrationIntensity: 2,
    blur: 5,
    saturation: 1.0,
    edgeCurvature: 1.0,
    innerShadowIntensity: 0.0,
    innerShadowSpread: 1.0,
    innerShadowBlur: 10.0,
    innerShadowOffset: [0.0, 0.0],
    innerShadowColor: [0.0, 0.0, 0.0],
  };

  if (!filterString.includes('liquid-glass')) {
    return defaults;
  }

  // Parse parameters from filter string like "liquid-glass(displacement: 70, ..., shadowIntensity: 0.5, shadowSpread: 2, shadowBlur: 4, shadowOffset: 2_3, shadowColor: 0_0_0)"
  const paramMatch = filterString.match(/liquid-glass\(([^)]+)\)/);
  if (!paramMatch) return defaults;

  const params = paramMatch[1].split(',').reduce((acc, param) => {
    const [key, value] = param.split(':').map((s) => s.trim());
    if (key && value) {
      if (key === 'shadowColor' || key === 'shadowOffset') {
        acc[key] = value;
      } else {
        const numVal = parseFloat(value);
        if (!isNaN(numVal)) {
          acc[key] = numVal;
        }
      }
    }
    return acc;
  }, {});

  const result = {
    displacementScale: params.displacement ?? defaults.displacementScale,
    aberrationIntensity: params.aberration ?? defaults.aberrationIntensity,
    blur: params.blur ?? defaults.blur,
    saturation: params.saturation ?? defaults.saturation,
    edgeCurvature: params.curvature ?? defaults.edgeCurvature,
    innerShadowIntensity: params.shadowIntensity ?? defaults.innerShadowIntensity,
    innerShadowSpread: params.shadowSpread ?? defaults.innerShadowSpread,
    innerShadowBlur: params.shadowBlur ?? defaults.innerShadowBlur,
    innerShadowOffset: defaults.innerShadowOffset,
    innerShadowColor: defaults.innerShadowColor,
  };

  if (params.shadowColor) {
    const color = parseColor(params.shadowColor);
    if (
      color &&
      typeof color.r === 'number' &&
      typeof color.g === 'number' &&
      typeof color.b === 'number'
    ) {
      result.innerShadowColor = [color.r / 255, color.g / 255, color.b / 255];
    }
  }

  if (params.shadowOffset) {
    // Accept formats like "10_5", "10,5", or "10 5"
    const raw = String(params.shadowOffset).trim();
    const parts = raw.includes('_') ? raw.split('_') : raw.split(/[\s,]+/);
    if (parts.length >= 2) {
      const ox = parseFloat(parts[0]);
      const oy = parseFloat(parts[1]);
      if (!isNaN(ox) && !isNaN(oy)) {
        result.innerShadowOffset = [ox, oy];
      }
    }
  }

  return result;
}

function clipRoundedRect(ctx, node) {
  const radius = node.radius;
  if (!radius || radius === 'none') return;
  const { x: normX, y: normY, w: normW, h: normH } = normalizeDimensions(node);
  const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(normW, normH, radius);
  ctx.beginPath();
  ctx.moveTo(normX + topLeft, normY);
  ctx.lineTo(normX + normW - topRight, normY);
  ctx.arcTo(normX + normW, normY, normX + normW, normY + topRight, topRight);
  ctx.lineTo(normX + normW, normY + normH - bottomRight);
  ctx.arcTo(normX + normW, normY + normH, normX + normW - bottomRight, normY + normH, bottomRight);
  ctx.lineTo(normX + bottomLeft, normY + normH);
  ctx.arcTo(normX, normY + normH, normX, normY + normH - bottomLeft, bottomLeft);
  ctx.lineTo(normX, normY + topLeft);
  ctx.arcTo(normX, normY, normX + topLeft, normY, topLeft);
  ctx.closePath();
  ctx.clip();
}
